// Copyright (c) StrangeBee 2023

import { ExtractProps } from '@Molecules/Details/Details';
import { FormatDate } from '@Molecules/Format';
import { ReactElement } from 'react';
import { Space } from 'antd';
import { Link } from 'react-router-dom';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { HTMLColors } from '@Constants/colors';
import { classList } from '@Helpers/className';
import { CustomStatusOutput } from 'thehive-sdk';
import { convertToLocaleId } from '@Helpers/locale';
import { ICONS } from '@Constants/iconsApp';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { cutString } from '@Helpers/string';
import { SeverityTag } from '@Atoms/Info/SeverityTag';

interface ExtractDataProps {
    locale: (...args: any[]) => any;
}

export const extractData = ({ locale }: ExtractDataProps): Record<string, ExtractProps> => ({
    '#computeTitle': {
        render: ({
            title,
            path,
            icon,
            severity,
        }: {
            title: string;
            path: string;
            icon: ReactElement;
            severity: number;
        }): ReactElement => (
            <div className="result-card__title">
                <Space>
                    {icon}
                    <SeverityTag severity={severity} />
                    <Link to={path}>{title}</Link>
                </Space>
            </div>
        ),
    },
    '#computeDate': {
        render: ({ date }: { date: number }): ReactElement => (
            <div className="result-card__date">
                <FormatDate icon date={date} />
            </div>
        ),
    },
    '#computeStatus': {
        name: '',
        isForce: false,
        render: ({ status }: { status: CustomStatusOutput }): ReactElement | null =>
            status ? (
                <ColorLabel
                    label={locale({
                        id: `alert.data.status.${convertToLocaleId(status?.value)}`,
                        defaultMessage: status?.value,
                    })}
                    color={status?.colour || HTMLColors.DARK_GRAY}
                    className={classList('status-tag')}
                    type="fill"
                />
            ) : null,
    },
    '#computeInfo': {
        render: ({
            type,
            source,
            sourceRef,
            artifacts,
        }: {
            type: string;
            sourceRef: string;
            source: string;
            artifacts: number;
        }): ReactElement => (
            <div className="alert-result-card__info">
                <Space>
                    <ICONS.TypeIcon />
                    {locale('search.results.type')}: {type}
                </Space>
                <Space>
                    <ICONS.ReferenceIcon />
                    {locale('search.results.reference')}: {sourceRef}
                </Space>
                <Space>
                    <ICONS.SourceIcon />
                    {locale('search.results.source')}: {source}
                </Space>
                <Space>
                    <ICONS.AttributesIcon />
                    {locale('search.results.attributes')}: {artifacts}
                </Space>
            </div>
        ),
    },
    '#computeDescription': {
        isForce: false,
        render: (description: string): ReactElement => (
            <div className="result-card__description">
                {description === 'N/A' ? locale('common.noDescription') : cutString(description, 140)}
            </div>
        ),
    },
    '#computeTags': {
        name: '',
        isForce: false,
        render: (tags: string[]): ReactElement => (
            <DisplayTag.List data={tags} limit={5}>
                {(tag) => <DisplayTag tag={tag} />}
            </DisplayTag.List>
        ),
    },
});
